// ============================================
// LISTASPACTADAS.JS — V2 modelo wishlist + snapshot + vigilancia
// ============================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { authHeaders, storage, STORAGE_KEYS } from './utils/storage';
import { fetchAuth } from './utils/sesion';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
} from './components/helpersClientes';
import SelectorMoneda, { useMoneda } from './components/SelectorMoneda';
import { useIvaModo, ToggleIva, conIvaSegun } from './components/SelectorIva';
import { DetalleColeccion } from './Colecciones';
import SelectorIconoLucide, { IconoLucide } from './components/SelectorIconoLucide';
import BarraAgregandoACotizacion from './components/BarraAgregandoACotizacion';
import { CampoPrecio } from './components/LineaPrecioEditable';
import { SkeletonListasCongeladas } from './components/Skeletons';

const LEYENDA_DEFAULT = 'Los precios están sujetos a variación del tipo de cambio. Vigentes mientras el tipo de cambio se mantenga en condiciones favorables.';

const ESTADO_COLOR = {
  ok:      { color: '#16a34a', bg: '#e8faf5', icono: '🟢', label: 'OK' },
  vigilar: { color: '#f59e0b', bg: '#fff8ed', icono: '🟡', label: 'Vigilar' },
  alerta:  { color: '#e05c5c', bg: '#fde8e8', icono: '🔴', label: 'Alerta' },
};

function fmtFecha(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

function hace(iso) {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

// ============================================
// RAÍZ
// ============================================

export default function ListasPactadas() {
  const [dash, setDash] = useState(null);
  const [listas, setListas] = useState([]);
  const [tcActual, setTcActual] = useState(null);
  const [tcFecha, setTcFecha] = useState(null);
  const [tcConsultado, setTcConsultado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [vigilando, setVigilando] = useState(false);
  const [modalNueva, setModalNueva] = useState(false);
  const [modalEditar, setModalEditar] = useState(null); // listaId
  const [vistaDetalle, setVistaDetalle] = useState(null); // lista base
  const [toast, setToast] = useState(null);
  // Ricardo dejo de usar las listas pactadas POR CLIENTE; la pantalla muestra ahora
  // SOLO las listas congeladas. El codigo por-cliente NO se borra (queda en una rama
  // inalcanzable, reversible): tab queda fijo en 'congeladas' (sin setter ni barra).
  const [tab] = useState('congeladas');
  const [coleccionCongelada, setColeccionCongelada] = useState(null); // detalle abierto
  // ALERTA-BAJO-COSTO: si se llega desde el aviso del vigilante, abrir ESA lista directamente.
  useEffect(() => {
    const pendiente = storage.get(STORAGE_KEYS.LISTA_ABRIR);
    if (pendiente && pendiente.id != null && pendiente.congelada !== false) {
      storage.remove(STORAGE_KEYS.LISTA_ABRIR);
      setColeccionCongelada({ id: Number(pendiente.id), nombre: pendiente.nombre || '' });
    }
  }, []);

  // useCallback: referencia estable. Se pasa como onExito/onError a DetalleColeccion; si
  // cambiara en cada render, re-disparaba el fetch de la lista (bucle de recarga). Ver fix.
  const mostrarToast = useCallback((m) => {
    setToast(m);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [dRes, lRes, tcRes] = await Promise.all([
        fetch(`${API_BASE_CLI}/api/listas/dashboard`, { headers: authHeaders() }).then(x => x.json()),
        fetch(`${API_BASE_CLI}/api/listas?todas=true`, { headers: authHeaders() }).then(x => x.json()),
        fetch(`${API_BASE_CLI}/api/tc/actual`).then(x => x.json()),
      ]);
      if (dRes.ok) setDash(dRes);
      if (lRes.ok) setListas(lRes.listas || []);
      if (tcRes.ok) {
        setTcActual(Number(tcRes.tc) || null);
        setTcFecha(tcRes.fecha);
        setTcConsultado(tcRes.consultado_en);
      }
    } finally {
      setCargando(false);
    }
  }, []);

  // El fetch por-cliente solo corre si se mostrara esa vista (hoy nunca, tab fijo en
  // 'congeladas'). Evita una llamada al dashboard de pactadas que ya no se usa.
  useEffect(() => { if (tab === 'cliente') cargar(); }, [cargar, tab]);

  const vigilarAhora = async () => {
    setVigilando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/listas/vigilar-todos`, { method: 'POST', headers: { ...authHeaders() } }).then(x => x.json());
      if (r.ok) {
        mostrarToast(`✅ Vigilancia: ${r.items_revisados} revisados · ${r.alerta || 0} alerta · ${r.vigilar || 0} vigilar`);
        cargar();
      } else {
        mostrarToast('⚠️ ' + (r.error || 'error'));
      }
    } catch (e) {
      mostrarToast('⚠️ ' + e.message);
    } finally {
      setVigilando(false);
    }
  };

  // Detalle de una LISTA CONGELADA (coleccion sin cliente): se ve con las MISMAS
  // tarjetas de La Tienda + sello de congelado, reusando el detalle de Colecciones.
  if (coleccionCongelada) {
    return (
      <DetalleColeccion
        coleccionBase={coleccionCongelada}
        onVolver={() => setColeccionCongelada(null)}
        onExito={mostrarToast}
        onError={mostrarToast}
      />
    );
  }

  if (vistaDetalle) {
    return (
      <DetalleLista
        listaBase={vistaDetalle}
        onVolver={() => { setVistaDetalle(null); cargar(); }}
        onToast={mostrarToast}
      />
    );
  }

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, padding: 20, background: COLOR_FONDO, minHeight: '100%' }}>
      {/* F3.B: barra "Agregando a la cotizacion X" (se auto-oculta si no hay modo). */}
      <BarraAgregandoACotizacion />
      <div style={{
        marginBottom: 18, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>
            📋 Listas pactadas
          </div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4, lineHeight: 1.5 }}>
            Listas de productos con precio CONGELADO (fijo por un tiempo). Se crean desde el
            cotizador con “Crear lista congelada”. Cada producto muestra su vigencia y avisa
            cuando esta por vencer.
          </div>
        </div>
      </div>

      {tab === 'congeladas' ? (
        <ListasCongeladasTab onAbrir={setColeccionCongelada} onToast={mostrarToast} />
      ) : (
      <>
      {/* Estado global */}
      <div style={{
        ...estiloTarjeta, padding: 14, marginBottom: 14,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        borderLeft: `4px solid ${COLOR_PRINCIPAL}`,
      }}>
        <div style={{ fontSize: 28 }}>📊</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Estado global
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_OSCURO }}>
            TC DOF: {tcActual != null ? `$${tcActual.toFixed(4)}` : '—'}
            <span style={{ marginLeft: 14, fontSize: 13, fontWeight: 500 }}>
              · {dash?.total_items || 0} items ·
              <span style={{ color: ESTADO_COLOR.ok.color, marginLeft: 6 }}>🟢 {dash?.por_estado?.ok || 0}</span>
              <span style={{ color: ESTADO_COLOR.vigilar.color, marginLeft: 6 }}>🟡 {dash?.por_estado?.vigilar || 0}</span>
              <span style={{ color: ESTADO_COLOR.alerta.color, marginLeft: 6 }}>🔴 {dash?.por_estado?.alerta || 0}</span>
            </span>
          </div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>
            {tcFecha ? `Fecha DOF: ${fmtFecha(tcFecha)} · ` : ''}
            {tcConsultado ? `actualizado ${hace(tcConsultado)}` : 'sin historial'}
          </div>
        </div>
        <button
          style={{ ...estiloBotonSecundario, opacity: vigilando ? 0.6 : 1 }}
          disabled={vigilando}
          onClick={vigilarAhora}
        >
          {vigilando ? 'Vigilando…' : '🔍 Vigilar ahora'}
        </button>
        <button style={estiloBotonPrimario} onClick={() => setModalNueva(true)}>
          + Nueva lista
        </button>
      </div>

      {cargando && <div style={{ color: COLOR_GRIS_CLARO }}>Cargando…</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {listas.map(l => (
          <TarjetaLista
            key={l.id}
            lista={l}
            onVerDetalle={() => setVistaDetalle(l)}
            onEditar={() => setModalEditar(l.id)}
            onDesactivar={async () => {
              if (!window.confirm(`¿Desactivar "${l.nombre}"?`)) return;
              const r = await fetch(`${API_BASE_CLI}/api/listas/${l.id}`, { method: 'DELETE', headers: { ...authHeaders() } }).then(x => x.json());
              if (r.ok) { mostrarToast('✅ Desactivada'); cargar(); }
              else mostrarToast('⚠️ ' + (r.error || 'error'));
            }}
          />
        ))}
        {!cargando && listas.length === 0 && (
          <div style={{ ...estiloTarjeta, padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>
            Sin listas. Empieza con <strong>+ Nueva lista</strong>.
          </div>
        )}
      </div>

      {(modalNueva || modalEditar) && (
        <ModalListaForm
          listaId={modalEditar || null}
          onCerrar={() => { setModalNueva(false); setModalEditar(null); }}
          onGuardado={() => {
            setModalNueva(false);
            setModalEditar(null);
            mostrarToast('✅ Guardada');
            cargar();
          }}
        />
      )}
      </>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff', padding: '10px 16px', borderRadius: 10,
          fontSize: 12, zIndex: 1500, fontFamily: PP,
        }}>{toast}</div>
      )}
    </div>
  );
}

// ============================================
// PESTANA "Listas congeladas" — panel FUSIONADO (rico + interior tipo tienda)
// ============================================
// Listas = colecciones con congelada=true. Panel exterior con tarjetas RICAS (estado de
// vencimiento POR LISTA, TC congelado de la lista, contador, acciones) + boton "Nueva
// lista" (crea una lista congelada VACIA con el TC del dia). Al abrir, reusa
// DetalleColeccion (tarjetas tipo tienda + sello + buscador para agregar).
const ESTADO_LISTA = {
  vigente:    { color: '#16a34a', bg: '#e8faf5', icono: '🟢', label: 'Vigente' },
  por_vencer: { color: '#c47d12', bg: '#fff8ed', icono: '🟡', label: 'Por vencer' },
  vencido:    { color: '#e05c5c', bg: '#fde8e8', icono: '🔴', label: 'Vencido' },
};

function ListasCongeladasTab({ onAbrir, onToast }) {
  const [listas, setListas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(null); // null | { modo:'crear' } | { modo:'editar', lista }

  const cargar = useCallback(() => {
    setCargando(true);
    fetchAuth(`${API_BASE_CLI}/api/colecciones?todas=true&congeladas=true`)
      .then(r => r.json())
      .then(d => { if (d.ok) setListas(d.colecciones || []); else onToast('⚠️ ' + (d.error || 'error')); })
      .catch(e => { if (e.code !== 'SESION_EXPIRADA') onToast('⚠️ ' + (e.message || 'error')); })
      .finally(() => setCargando(false));
  }, [onToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const toggleActiva = async (c) => {
    const activar = c.activa === false;
    if (!activar && !window.confirm(`¿Desactivar la lista "${c.nombre}"? Dejara de mostrarse activa.`)) return;
    try {
      const r = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${c.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activa: activar }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { onToast('⚠️ ' + (d.error || 'error')); return; }
      onToast(activar ? '✅ Lista activada' : '✅ Lista desactivada');
      cargar();
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') onToast('⚠️ ' + (e.message || 'error')); }
  };

  return (
    <div>
      {/* La barra "Agregando a la cotizacion X" la pinta el componente PADRE (raiz de
          ListasPactadas) ANTES de montar esta pestana; aqui NO se repite para que en
          "modo agregar" aparezca UNA sola barra (antes salia duplicada: padre + pestana). */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button style={estiloBotonPrimario} onClick={() => setModal({ modo: 'crear' })}>
          <LucideIcons.Plus size={16} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Nueva lista
        </button>
      </div>

      {cargando ? (
        <SkeletonListasCongeladas />
      ) : listas.length === 0 ? (
        <div style={{ ...estiloTarjeta, padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>
          Aun no hay listas pactadas. Crea una con <strong>Nueva lista</strong>, o desde el
          cotizador con <strong>“Crear lista congelada”</strong>.
        </div>
      ) : (
        // Tarjetas tipo Colecciones SIEMPRE (grid auto-fill: colapsa por ancho — 1 col en
        // celular, 2-3 en tablet, mas en PC; en V y H). Mismo grid que Colecciones.
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))' }}>
          {listas.map(c => (
            <TarjetaListaCongelada
              key={c.id}
              lista={c}
              onAbrir={() => onAbrir(c)}
              onEditar={() => setModal({ modo: 'editar', lista: c })}
              onToggleActiva={() => toggleActiva(c)}
            />
          ))}
        </div>
      )}

      {modal && (
        <ModalListaCongelada
          modo={modal.modo}
          lista={modal.lista || null}
          onCerrar={() => setModal(null)}
          onGuardado={(coleccion, esNueva) => {
            setModal(null);
            onToast(esNueva ? '✅ Lista congelada creada' : '✅ Lista actualizada');
            cargar();
            // Al crear nueva, entrar directo al detalle para agregar productos.
            if (esNueva && coleccion) onAbrir(coleccion);
          }}
        />
      )}
    </div>
  );
}

// Tarjeta de lista pactada (vista UNICA, mismo molde que TarjetaColeccion de Colecciones).
// Conserva los datos de lista: estado, vencidos/por-vencer, N productos, vigencia y TC congelado.
function TarjetaListaCongelada({ lista, onAbrir, onEditar, onToggleActiva }) {
  const color = lista.color || COLOR_PRINCIPAL;
  const total = lista.items_total || 0;
  const cong = lista.items_congelados || 0;
  const inactiva = lista.activa === false;
  const estadoCfg = cong > 0 ? (ESTADO_LISTA[lista.estado_lista] || ESTADO_LISTA.vigente) : null;

  return (
    <div style={{
      ...estiloTarjeta, padding: 16, borderRadius: 12, opacity: inactiva ? 0.6 : 1,
      borderLeft: `4px solid ${estadoCfg && !inactiva ? estadoCfg.color : color}`,
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      {/* Cabecera: icono + nombre + chip inactiva (mismo molde que TarjetaColeccion) */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{
          width: 42, height: 42, borderRadius: 10, flexShrink: 0,
          background: color + '22', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <IconoLucide nombre={lista.icono || 'snowflake'} size={22} color={color} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OSCURO, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {lista.nombre}
            {inactiva && (
              <span style={{ fontSize: 10, fontWeight: 600, color: COLOR_GRIS_CLARO, background: '#f3f4f4', padding: '1px 6px', borderRadius: 20 }}>Inactiva</span>
            )}
          </div>
          {lista.descripcion && (
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, lineHeight: 1.35, marginTop: 2 }}>{lista.descripcion}</div>
          )}
        </div>
      </div>

      {/* Estado + por vencer/vencidos */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {estadoCfg ? (
          <span style={{ fontSize: 11, fontWeight: 700, color: estadoCfg.color, background: estadoCfg.bg, padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}>
            {estadoCfg.icono} {estadoCfg.label}
          </span>
        ) : <span style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>Sin productos congelados</span>}
        {lista.items_vencidos > 0 && <span style={{ fontSize: 11, color: ESTADO_LISTA.vencido.color, fontWeight: 700 }}>🔴 {lista.items_vencidos}</span>}
        {lista.items_por_vencer > 0 && <span style={{ fontSize: 11, color: ESTADO_LISTA.por_vencer.color, fontWeight: 700 }}>🟡 {lista.items_por_vencer}</span>}
      </div>

      {/* Datos: productos · vigencia · TC congelado (+ fecha) */}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: COLOR_GRIS }}>
        <span><strong style={{ color: COLOR_OSCURO }}>{total}</strong> producto{total === 1 ? '' : 's'}</span>
        <span>Vigencia: <strong style={{ color: COLOR_OSCURO }}>{lista.congelado_dias ? `${lista.congelado_dias} d` : 'indef.'}</strong></span>
        <span>
          TC: <strong style={{ color: COLOR_OSCURO }}>{lista.tc_congelado != null ? `$${Number(lista.tc_congelado).toFixed(4)}` : '—'}</strong>
          {lista.tc_congelado_fecha && <span style={{ color: COLOR_GRIS_CLARO }}> · {fmtFecha(lista.tc_congelado_fecha)}</span>}
        </span>
      </div>

      {/* Acciones: Ver detalle (primario) + Editar (icono) + Activar/Desactivar (icono) — molde Colecciones */}
      <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
        <button onClick={onAbrir} style={{ ...estiloBotonPrimario, flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>Ver detalle</button>
        <button onClick={onEditar} title="Editar info" style={estiloBotonIconoLP}>
          <LucideIcons.Pencil size={16} />
        </button>
        <button onClick={onToggleActiva} title={inactiva ? 'Activar' : 'Desactivar'} style={{ ...estiloBotonIconoLP, color: inactiva ? '#16a34a' : '#e05c5c' }}>
          {inactiva ? <LucideIcons.Power size={16} /> : <LucideIcons.PowerOff size={16} />}
        </button>
      </div>
    </div>
  );
}

// Boton-icono cuadrado (mismo estilo que estiloBotonIcono de Colecciones) para Editar / Activar.
const estiloBotonIconoLP = {
  background: 'transparent', border: `1px solid ${COLOR_BORDE}`, cursor: 'pointer',
  padding: 8, borderRadius: 8, color: COLOR_GRIS,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

// Modal crear/editar info de una lista congelada (nombre, vigencia, color, icono).
const COLORES_PRESET_LC = ['#93C5FD', '#3B82F6', '#5c9adb', '#5cd4b0', '#e0a05c', '#9a5cdb', '#e05c5c', '#f5b041', '#2f5f9e'];
function ModalListaCongelada({ modo, lista, onCerrar, onGuardado }) {
  const esEditar = modo === 'editar';
  const [nombre, setNombre] = useState(lista?.nombre || '');
  const [dias, setDias] = useState(lista?.congelado_dias != null ? String(lista.congelado_dias) : '30');
  const [color, setColor] = useState(lista?.color || '#5c9adb');
  const [icono, setIcono] = useState(lista?.icono || 'snowflake');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const guardar = async () => {
    const nom = nombre.trim();
    if (!nom) { setError('El nombre es obligatorio.'); return; }
    const d = Math.trunc(Number(dias));
    const diasVal = Number.isFinite(d) && d > 0 ? d : null;
    setGuardando(true); setError(null);
    try {
      let r, body;
      if (esEditar) {
        body = { nombre: nom, congelado_dias: diasVal, color, icono };
        r = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${lista.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
      } else {
        // Crear lista congelada VACIA: el backend le congela el TC del dia.
        body = { nombre: nom, congelada: true, dias_vigencia: diasVal, color, icono };
        r = await fetchAuth(`${API_BASE_CLI}/api/colecciones`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
      }
      const dRes = await r.json().catch(() => ({}));
      if (!r.ok || !dRes.ok) { setError(dRes.error || 'No se pudo guardar.'); setGuardando(false); return; }
      onGuardado(dRes.coleccion, !esEditar);
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setError('Error: ' + (e.message || ''));
      setGuardando(false);
    }
  };

  return (
    <div onClick={onCerrar} style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
        fontFamily: PP, color: COLOR_GRIS, padding: 24, maxHeight: 'calc(var(--beep-vh) * 92)', overflowY: 'auto',
      }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: COLOR_OSCURO, marginBottom: 4 }}>
          {esEditar ? 'Editar lista congelada' : 'Nueva lista congelada'}
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 16, lineHeight: 1.4 }}>
          {esEditar
            ? 'Cambia el nombre, la vigencia y el aspecto. El TC congelado de la lista no cambia.'
            : 'Se crea vacia y se le congela el TC de hoy. Luego agregale productos con el buscador.'}
        </div>

        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginBottom: 5 }}>Nombre</div>
        <input autoFocus value={nombre} onChange={(e) => setNombre(e.target.value)}
          placeholder="Ej. Lista Obra Norte" style={{ ...estiloInput, fontSize: 14, marginBottom: 14 }} />

        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginBottom: 5 }}>Dias de vigencia del precio congelado</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <input type="number" min="1" step="1" value={dias} onChange={(e) => setDias(e.target.value)}
            style={{ ...estiloInput, fontSize: 14, marginBottom: 0, width: 110 }} />
          <span style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>dias (vacio = indefinida)</span>
        </div>

        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginBottom: 6 }}>Color</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {COLORES_PRESET_LC.map(c => (
            <button key={c} type="button" onClick={() => setColor(c)} title={c}
              style={{ width: 24, height: 24, borderRadius: 6, background: c, cursor: 'pointer', border: color === c ? `2px solid ${COLOR_OSCURO}` : `1px solid ${COLOR_BORDE}` }} />
          ))}
        </div>

        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginBottom: 6 }}>Icono</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: (color || '#5c9adb') + '22', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconoLucide nombre={icono} size={18} color={color || '#5c9adb'} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SelectorIconoLucide value={icono} onChange={(n) => setIcono(n)} />
          </div>
        </div>

        {error && (
          <div style={{ marginBottom: 12, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>⚠️ {error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button style={{ ...estiloBotonPrimario, opacity: guardando ? 0.6 : 1 }} onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : (esEditar ? 'Guardar' : 'Crear lista')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Tarjeta en la vista principal
// ============================================

function TarjetaLista({ lista, onVerDetalle, onEditar, onDesactivar }) {
  const hoy = new Date();
  const hasta = new Date(lista.vigente_hasta);
  const vigente = hasta >= hoy;
  const tieneAlertas = (lista.items_alerta || 0) > 0;
  const tieneVigilar = (lista.items_vigilar || 0) > 0;

  const bordeColor = !lista.activa || !vigente ? COLOR_GRIS_CLARO
    : tieneAlertas ? ESTADO_COLOR.alerta.color
    : tieneVigilar ? ESTADO_COLOR.vigilar.color
    : ESTADO_COLOR.ok.color;

  return (
    <div style={{
      ...estiloTarjeta, padding: 16,
      borderLeft: `4px solid ${bordeColor}`,
      opacity: lista.activa === false ? 0.55 : 1,
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap', marginBottom: 10,
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO }}>
            📋 {lista.nombre}
            <span style={{
              marginLeft: 8, padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600,
              background: lista.activa ? '#e8faf5' : '#f3f4f4',
              color: lista.activa ? '#1D9E75' : COLOR_GRIS_CLARO,
            }}>
              {lista.activa ? '🟢 Activa' : 'Inactiva'}
            </span>
            {tieneAlertas && (
              <span style={{
                marginLeft: 6, padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600,
                background: ESTADO_COLOR.alerta.bg, color: ESTADO_COLOR.alerta.color,
              }}>
                🚨 {lista.items_alerta} alerta{lista.items_alerta === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {lista.cliente_id && (
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
              Cliente ID: {lista.cliente_id}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={estiloBotonPrimario} onClick={onVerDetalle}>Ver detalle</button>
          <button style={estiloBotonSecundario} onClick={onEditar}>Editar info</button>
          {lista.activa && <button style={{ ...estiloBotonSecundario, color: '#e05c5c' }} onClick={onDesactivar}>Desactivar</button>}
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, fontSize: 12,
      }}>
        <div>
          <div style={{ color: COLOR_GRIS_CLARO, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Vigencia</div>
          <div style={{ color: COLOR_GRIS, fontWeight: 600 }}>
            hasta {fmtFecha(lista.vigente_hasta)}
          </div>
          <div style={{ color: vigente ? COLOR_GRIS_CLARO : '#e05c5c', fontSize: 11 }}>
            {vigente ? 'vigente' : 'expirada'}
          </div>
        </div>
        <div>
          <div style={{ color: COLOR_GRIS_CLARO, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Items pactados</div>
          <div style={{ color: COLOR_OSCURO, fontWeight: 700, fontSize: 16 }}>
            {lista.items_total || 0}
          </div>
        </div>
        {(tieneAlertas || tieneVigilar) && (
          <div>
            <div style={{ color: COLOR_GRIS_CLARO, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Estado</div>
            <div style={{ fontSize: 12 }}>
              {tieneAlertas && <span style={{ color: ESTADO_COLOR.alerta.color, fontWeight: 700 }}>🔴 {lista.items_alerta}</span>}
              {tieneAlertas && tieneVigilar && <span style={{ color: COLOR_GRIS_CLARO }}> · </span>}
              {tieneVigilar && <span style={{ color: ESTADO_COLOR.vigilar.color, fontWeight: 700 }}>🟡 {lista.items_vigilar}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Modal crear/editar lista — información general
// ============================================

function ModalListaForm({ listaId, onCerrar, onGuardado }) {
  const [form, setForm] = useState({
    nombre: '', cliente_id: '', vigente_desde: '', vigente_hasta: '',
    leyenda_cotizacion: LEYENDA_DEFAULT, notas: '',
  });
  const [cargando, setCargando] = useState(!!listaId);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!listaId) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_CLI}/api/listas/${listaId}`, { headers: authHeaders() }).then(x => x.json());
        if (r.ok && r.lista) {
          const l = r.lista;
          setForm({
            nombre: l.nombre || '',
            cliente_id: l.cliente_id != null ? String(l.cliente_id) : '',
            vigente_desde: (l.vigente_desde || '').slice(0, 10),
            vigente_hasta: (l.vigente_hasta || '').slice(0, 10),
            leyenda_cotizacion: l.leyenda_cotizacion || LEYENDA_DEFAULT,
            notas: l.notas || '',
          });
        }
      } finally { setCargando(false); }
    })();
  }, [listaId]);

  const guardar = async () => {
    if (!form.nombre.trim()) { setError('Nombre requerido'); return; }
    if (!form.vigente_desde || !form.vigente_hasta) { setError('Vigencia requerida'); return; }
    setError(null);
    setGuardando(true);
    try {
      const body = {
        nombre: form.nombre.trim(),
        cliente_id: form.cliente_id ? Number(form.cliente_id) : null,
        vigente_desde: form.vigente_desde,
        vigente_hasta: form.vigente_hasta,
        leyenda_cotizacion: form.leyenda_cotizacion,
        notas: form.notas || null,
      };
      const url = listaId ? `${API_BASE_CLI}/api/listas/${listaId}` : `${API_BASE_CLI}/api/listas`;
      const r = await fetch(url, {
        method: listaId ? 'PATCH' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      onGuardado && onGuardado(r.lista);
    } catch (e) {
      setError(e.message);
    } finally { setGuardando(false); }
  };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
          fontFamily: PP, color: COLOR_GRIS, maxHeight: 'calc(var(--beep-vh) * 92)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${COLOR_BORDE}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO }}>
            {listaId ? 'Editar lista pactada' : 'Nueva lista pactada'}
          </div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
            Los items (productos + mano de obra) se pactan uno por uno desde el detalle o desde el catálogo.
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {cargando ? (
            <div style={{ color: COLOR_GRIS_CLARO }}>Cargando…</div>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Nombre *</div>
                <input style={estiloInput} value={form.nombre}
                  onChange={(e) => setForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej. Bodegas Alianza 2026" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Cliente ID (grupo (cliente))</div>
                <input style={estiloInput} type="number" value={form.cliente_id}
                  onChange={(e) => setForm(f => ({ ...f, cliente_id: e.target.value }))}
                  placeholder="id de grupo (cliente) (opcional)" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <div style={lbl}>Vigente desde *</div>
                  <input style={estiloInput} type="date" value={form.vigente_desde}
                    onChange={(e) => setForm(f => ({ ...f, vigente_desde: e.target.value }))} />
                </div>
                <div>
                  <div style={lbl}>Vigente hasta *</div>
                  <input style={estiloInput} type="date" value={form.vigente_hasta}
                    onChange={(e) => setForm(f => ({ ...f, vigente_hasta: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Leyenda en cotizaciones</div>
                <textarea style={{ ...estiloInput, minHeight: 70, resize: 'vertical' }}
                  value={form.leyenda_cotizacion}
                  onChange={(e) => setForm(f => ({ ...f, leyenda_cotizacion: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Notas internas</div>
                <textarea style={{ ...estiloInput, minHeight: 50, resize: 'vertical' }}
                  value={form.notas}
                  onChange={(e) => setForm(f => ({ ...f, notas: e.target.value }))} />
              </div>
              {error && (
                <div style={{ marginBottom: 12, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>
                  ⚠️ {error}
                </div>
              )}
            </>
          )}
        </div>
        <div style={{
          padding: '14px 24px', borderTop: `1px solid ${COLOR_BORDE}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button style={estiloBotonSecundario} onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button style={{ ...estiloBotonPrimario, opacity: guardando || cargando ? 0.6 : 1 }}
            onClick={guardar} disabled={guardando || cargando}>
            {guardando ? 'Guardando…' : listaId ? 'Guardar' : 'Crear lista'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Detalle — vista AGRUPADA por tipo (Cableado / Mano de obra / Otros)
// ============================================

function DetalleLista({ listaBase, onVolver, onToast }) {
  const [grupos, setGrupos] = useState({ cables: [], mano_obra: [], otros: [] });
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState('todos');
  const [modalEditarPrecio, setModalEditarPrecio] = useState(null);
  const { formatear } = useMoneda();
  // Switch global Con/Sin IVA (mismo helper que las tarjetas de La Tienda).
  const [ivaModo, setIvaModo] = useIvaModo();

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/listas/${listaBase.id}/items-agrupados`, { headers: authHeaders() }).then(x => x.json());
      if (r.ok) setGrupos(r.grupos || { cables: [], mano_obra: [], otros: [] });
    } finally { setCargando(false); }
  }, [listaBase.id]);

  useEffect(() => { cargar(); }, [cargar]);

  const conteos = useMemo(() => {
    let total = 0, ok = 0, vigilar = 0, alerta = 0;
    const todos = [
      ...grupos.cables.flatMap(g => g.items),
      ...grupos.mano_obra.flatMap(g => g.items),
      ...grupos.otros,
    ];
    for (const it of todos) {
      total++;
      const e = it.vigilancia?.estado || 'ok';
      if (e === 'ok') ok++;
      else if (e === 'vigilar') vigilar++;
      else if (e === 'alerta') alerta++;
    }
    return { total, ok, vigilar, alerta };
  }, [grupos]);

  const filtrarItems = (items) => {
    if (filtro === 'todos') return items;
    return items.filter(it => (it.vigilancia?.estado || 'ok') === filtro);
  };

  const quitarItem = async (itemId) => {
    if (!window.confirm('¿Quitar este item?')) return;
    const r = await fetch(`${API_BASE_CLI}/api/listas/${listaBase.id}/items/${itemId}`, { method: 'DELETE', headers: { ...authHeaders() } }).then(x => x.json());
    if (r.ok) { onToast('✅ Quitado'); cargar(); }
    else onToast('⚠️ ' + (r.error || 'error'));
  };

  const quitarGrupo = async (tipoGrupo, referenciaGrupo, etiqueta) => {
    if (!window.confirm(`¿Quitar TODOS los items de "${etiqueta}"?`)) return;
    const r = await fetch(`${API_BASE_CLI}/api/listas/${listaBase.id}/items/grupo`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ tipo_grupo: tipoGrupo, referencia_grupo: referenciaGrupo }),
    }).then(x => x.json());
    if (r.ok) { onToast(`✅ ${r.items_desactivados} items quitados`); cargar(); }
    else onToast('⚠️ ' + (r.error || 'error'));
  };

  const grupoCablesFiltrados = grupos.cables
    .map(g => ({ ...g, items: filtrarItems(g.items) }))
    .filter(g => g.items.length > 0);
  const grupoMOFiltrados = grupos.mano_obra
    .map(g => ({ ...g, items: filtrarItems(g.items) }))
    .filter(g => g.items.length > 0);
  const otrosFiltrados = filtrarItems(grupos.otros);

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, padding: 20, background: COLOR_FONDO, minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
        <button style={estiloBotonSecundario} onClick={onVolver}>← Volver</button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>📋 {listaBase.nombre}</div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>
            Vigencia hasta {fmtFecha(listaBase.vigente_hasta)} · {conteos.total} items pactados
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <ToggleIva modo={ivaModo} onCambio={setIvaModo} compacto />
          <SelectorMoneda compacto />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {[
          { id: 'todos',   label: `Todos (${conteos.total})` },
          { id: 'ok',      label: `🟢 OK (${conteos.ok})` },
          { id: 'vigilar', label: `🟡 Vigilar (${conteos.vigilar})` },
          { id: 'alerta',  label: `🔴 Alerta (${conteos.alerta})` },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFiltro(f.id)}
            style={{
              padding: '6px 12px', fontSize: 12, fontFamily: PP, cursor: 'pointer',
              border: 'none', borderRadius: 99,
              background: filtro === f.id ? COLOR_PRINCIPAL : '#fff',
              color: filtro === f.id ? '#fff' : COLOR_GRIS,
              fontWeight: filtro === f.id ? 700 : 500,
            }}
          >{f.label}</button>
        ))}
      </div>

      {cargando && <div style={{ color: COLOR_GRIS_CLARO }}>Cargando…</div>}

      {!cargando && conteos.total === 0 && (
        <div style={{ ...estiloTarjeta, padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>
          Aún no hay items pactados. Agrégalos desde el catálogo (Cableado o Mano de obra).
        </div>
      )}

      {grupoCablesFiltrados.length > 0 && (
        <SeccionTipo
          titulo="🔌 CABLEADO PACTADO"
          subtitulo={`${grupos.cables.length} cable(s), ${grupos.cables.reduce((n, g) => n + g.items.length, 0)} items`}
        >
          <div style={{ display: 'grid', gap: 12 }}>
            {grupoCablesFiltrados.map(g => (
              <TarjetaGrupoCable
                key={g.cable_id}
                grupo={g}
                formatear={formatear}
                ivaModo={ivaModo}
                onEditarItem={(it) => setModalEditarPrecio(it)}
                onQuitarItem={(it) => quitarItem(it.id)}
                onQuitarGrupo={() => quitarGrupo('cable', g.cable_id, g.cable_nombre || `Cable ${g.cable_id}`)}
              />
            ))}
          </div>
        </SeccionTipo>
      )}

      {grupoMOFiltrados.length > 0 && (
        <SeccionTipo
          titulo="🔨 MANO DE OBRA PACTADA"
          subtitulo={`${grupos.mano_obra.length} paquete(s), ${grupos.mano_obra.reduce((n, g) => n + g.items.length, 0)} items`}
        >
          <div style={{ display: 'grid', gap: 12 }}>
            {grupoMOFiltrados.map(g => (
              <TarjetaGrupoMO
                key={g.categoria_id}
                grupo={g}
                formatear={formatear}
                ivaModo={ivaModo}
                onEditarItem={(it) => setModalEditarPrecio(it)}
                onQuitarItem={(it) => quitarItem(it.id)}
                onQuitarGrupo={() => quitarGrupo('mano_obra', g.categoria_id, g.categoria_nombre || `Categoría ${g.categoria_id}`)}
              />
            ))}
          </div>
        </SeccionTipo>
      )}

      {otrosFiltrados.length > 0 && (
        <SeccionTipo titulo="📦 OTROS ITEMS" subtitulo={`${otrosFiltrados.length} items`}>
          <div style={{ display: 'grid', gap: 12 }}>
            {otrosFiltrados.map(it => (
              <TarjetaItemSimple
                key={it.id}
                item={it}
                formatear={formatear}
                ivaModo={ivaModo}
                onEditar={() => setModalEditarPrecio(it)}
                onQuitar={() => quitarItem(it.id)}
              />
            ))}
          </div>
        </SeccionTipo>
      )}

      {modalEditarPrecio && (
        <ModalEditarPrecio
          listaId={listaBase.id}
          item={modalEditarPrecio}
          onCerrar={() => setModalEditarPrecio(null)}
          onGuardado={() => {
            setModalEditarPrecio(null);
            onToast('✅ Precio actualizado');
            cargar();
          }}
        />
      )}
    </div>
  );
}

function SeccionTipo({ titulo, subtitulo, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        marginBottom: 12, padding: '10px 0',
        borderBottom: `2px solid ${COLOR_OSCURO}`,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, letterSpacing: 0.3 }}>
          {titulo}
        </div>
        {subtitulo && (
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
            {subtitulo}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function TarjetaGrupoCable({ grupo, formatear, ivaModo, onEditarItem, onQuitarItem, onQuitarGrupo }) {
  const [expandido, setExpandido] = useState(true);
  const cfg = ESTADO_COLOR[grupo.estado_global || 'ok'];
  const niveles = [
    { id: 'aparente', label: 'Aparente', icono: '🟢' },
    { id: 'guiada', label: 'Guiada', icono: '🔵' },
    { id: 'no_guiada', label: 'No guiada', icono: '🟡' },
    { id: 'saturada', label: 'Saturada', icono: '🔴' },
  ];
  const itemsPorNivel = new Map();
  for (const it of grupo.items) itemsPorNivel.set(it.nivel, it);

  return (
    <div style={{ ...estiloTarjeta, padding: 16, borderLeft: `4px solid ${cfg.color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <button
          style={{ ...estiloBotonSecundario, padding: '4px 8px', fontSize: 11 }}
          onClick={() => setExpandido(e => !e)}
        >{expandido ? '▾' : '▸'}</button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>
            {grupo.cable_nombre || `Cable ${grupo.cable_id}`}
          </div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>
            {grupo.items.length} nivel(es) · margen prom <strong style={{ color: margenColor(grupo.margen_promedio) }}>
              {fmtPct(grupo.margen_promedio)} {margenIcono(grupo.margen_promedio)}
            </strong>
          </div>
        </div>
        <button style={{ ...estiloBotonSecundario, color: '#e05c5c' }} onClick={onQuitarGrupo}>
          🗑 Quitar todos
        </button>
      </div>

      {expandido && (
        <div style={{ display: 'grid', gap: 6 }}>
          {niveles.map(n => {
            const it = itemsPorNivel.get(n.id);
            if (!it) return null;
            const margen = it.vigilancia?.margen_actual_pct ?? it.margen_snapshot_pct;
            return (
              <div key={n.id} style={{
                display: 'grid', gridTemplateColumns: '140px 1fr 1fr 90px', gap: 10, alignItems: 'center',
                padding: '8px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLOR_OSCURO }}>{n.icono} {n.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>
                  {formatear(conIvaSegun(ivaModo, it.precio_pactado_mxn), 'MXN')}/m
                </div>
                <div style={{ fontSize: 11, color: margenColor(margen), fontWeight: 600 }}>
                  margen {fmtPct(margen)} {margenIcono(margen)}
                </div>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <button style={btnIcon} onClick={() => onEditarItem(it)} title="Editar">✏️</button>
                  <button style={{ ...btnIcon, color: '#e05c5c' }} onClick={() => onQuitarItem(it)} title="Quitar">🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TarjetaGrupoMO({ grupo, formatear, ivaModo, onEditarItem, onQuitarItem, onQuitarGrupo }) {
  const [expandido, setExpandido] = useState(true);
  const cfg = ESTADO_COLOR[grupo.estado_global || 'ok'];
  const color = grupo.categoria_color || COLOR_PRINCIPAL;
  const niveles = [
    { id: 'basica', label: 'Básica', icono: '🟢' },
    { id: 'estandar', label: 'Estándar', icono: '🔵' },
    { id: 'altura', label: 'Altura', icono: '🟡' },
    { id: 'compleja', label: 'Compleja', icono: '🔴' },
  ];

  const itemsPorTipoNivel = new Map();
  for (const it of grupo.items) itemsPorTipoNivel.set(`${it.tipo_mo}|${it.nivel || ''}`, it);

  const renderRow = (tipoMO) => (
    <div style={{ display: 'grid', gap: 6 }}>
      {niveles.map(n => {
        const it = itemsPorTipoNivel.get(`${tipoMO}|${n.id}`);
        if (!it) return null;
        const margen = it.vigilancia?.margen_actual_pct ?? it.margen_snapshot_pct;
        return (
          <div key={n.id} style={{
            display: 'grid', gridTemplateColumns: '140px 1fr 1fr 90px', gap: 10, alignItems: 'center',
            padding: '6px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
          }}>
            <div style={{ fontSize: 12, color: COLOR_OSCURO }}>{n.icono} {n.label}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>
              {formatear(it.precio_pactado_mxn, 'MXN')}
            </div>
            <div style={{ fontSize: 11, color: margenColor(margen), fontWeight: 600 }}>
              margen {fmtPct(margen)} {margenIcono(margen)}
            </div>
            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
              <button style={btnIcon} onClick={() => onEditarItem(it)} title="Editar">✏️</button>
              <button style={{ ...btnIcon, color: '#e05c5c' }} onClick={() => onQuitarItem(it)} title="Quitar">🗑</button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const tieneInst = grupo.items.some(i => i.tipo_mo === 'instalacion');
  const tieneServ = grupo.items.some(i => i.tipo_mo === 'servicio');
  const tieneFijo = grupo.items.some(i => i.tipo_mo === 'fijo');

  return (
    <div style={{ ...estiloTarjeta, padding: 16, borderLeft: `4px solid ${cfg.color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <button
          style={{ ...estiloBotonSecundario, padding: '4px 8px', fontSize: 11 }}
          onClick={() => setExpandido(e => !e)}
        >{expandido ? '▾' : '▸'}</button>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: color + '22', color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20,
        }}>{grupo.categoria_icono || '📦'}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>
            {grupo.categoria_nombre || `Categoría ${grupo.categoria_id}`}
          </div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>
            {grupo.items.length} item(s) · margen prom <strong style={{ color: margenColor(grupo.margen_promedio) }}>
              {fmtPct(grupo.margen_promedio)} {margenIcono(grupo.margen_promedio)}
            </strong>
          </div>
        </div>
        <button style={{ ...estiloBotonSecundario, color: '#e05c5c' }} onClick={onQuitarGrupo}>
          🗑 Quitar paquete
        </button>
      </div>

      {expandido && (
        <div style={{ display: 'grid', gap: 12 }}>
          {tieneInst && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_OSCURO, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
                INSTALACIÓN
              </div>
              {renderRow('instalacion')}
            </div>
          )}
          {tieneServ && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_OSCURO, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
                SERVICIO
              </div>
              {renderRow('servicio')}
            </div>
          )}
          {tieneFijo && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_OSCURO, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
                FIJO
              </div>
              {grupo.items.filter(i => i.tipo_mo === 'fijo').map(it => {
                const margen = it.vigilancia?.margen_actual_pct ?? it.margen_snapshot_pct;
                return (
                  <div key={it.id} style={{
                    display: 'grid', gridTemplateColumns: '140px 1fr 1fr 90px', gap: 10, alignItems: 'center',
                    padding: '6px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
                  }}>
                    <div style={{ fontSize: 12, color: COLOR_OSCURO }}>Precio fijo</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>
                      {formatear(conIvaSegun(ivaModo, it.precio_pactado_mxn), 'MXN')}
                    </div>
                    <div style={{ fontSize: 11, color: margenColor(margen), fontWeight: 600 }}>
                      margen {fmtPct(margen)} {margenIcono(margen)}
                    </div>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button style={btnIcon} onClick={() => onEditarItem(it)} title="Editar">✏️</button>
                      <button style={{ ...btnIcon, color: '#e05c5c' }} onClick={() => onQuitarItem(it)} title="Quitar">🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TarjetaItemSimple({ item, formatear, ivaModo, onEditar, onQuitar }) {
  const margen = item.vigilancia?.margen_actual_pct ?? item.margen_snapshot_pct;
  const cfg = ESTADO_COLOR[item.vigilancia?.estado || 'ok'];
  return (
    <div style={{ ...estiloTarjeta, padding: 14, borderLeft: `4px solid ${cfg.color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{
          width: 48, height: 48, borderRadius: 8, background: COLOR_FONDO,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, overflow: 'hidden',
        }}>
          {item.imagen_url
            ? <img src={item.imagen_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : '📦'}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>{item.nombre}</div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>{item.tipo} · {item.referencia_id}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO }}>
            {formatear(conIvaSegun(ivaModo, item.precio_pactado_mxn), 'MXN')}
          </div>
          <div style={{ fontSize: 11, color: margenColor(margen), fontWeight: 600 }}>
            {fmtPct(margen)} {margenIcono(margen)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button style={btnIcon} onClick={onEditar} title="Editar">✏️</button>
          <button style={{ ...btnIcon, color: '#e05c5c' }} onClick={onQuitar} title="Quitar">🗑</button>
        </div>
      </div>
    </div>
  );
}

function margenColor(pct) {
  if (pct == null) return COLOR_GRIS_CLARO;
  if (pct >= 25) return '#16a34a';
  if (pct >= 15) return '#f59e0b';
  return '#e05c5c';
}
function margenIcono(pct) {
  if (pct == null) return '';
  if (pct >= 25) return '🟢';
  if (pct >= 15) return '🟡';
  return '🔴';
}
function fmtPct(p) {
  if (p == null) return '—';
  return `${Number(p).toFixed(1)}%`;
}

function ModalEditarPrecio({ listaId, item, onCerrar, onGuardado }) {
  const [valor, setValor] = useState(String(item.precio_pactado_mxn));
  const [notas, setNotas] = useState(item.notas || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const costo = Number(item.costo_snapshot_mxn);
  const margenEst = costo > 0 ? ((Number(valor) - costo) / costo * 100) : null;

  const guardar = async () => {
    const p = Number(valor);
    if (String(valor).trim() === '' || !Number.isFinite(p) || p < 0) { setError('Precio inválido'); return; }
    setError(null);
    setGuardando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/listas/${listaId}/items/${item.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ precio_pactado_mxn: p, notas }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      onGuardado && onGuardado(r.item);
    } catch (e) { setError(e.message); } finally { setGuardando(false); }
  };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420,
          fontFamily: PP, color: COLOR_GRIS, padding: 24,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>
          Reajustar precio
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 16 }}>
          {item.nombre}
        </div>
        <div style={{ fontSize: 12, marginBottom: 12 }}>
          Costo snapshot (MXN): <strong>${costo.toFixed(2)}</strong>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={lbl}>Precio pactado MXN *</div>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: 9, color: COLOR_GRIS_CLARO, fontSize: 13 }}>$</span>
            <CampoPrecio style={{ ...estiloInput, paddingLeft: 22 }}
              value={valor}
              onChange={(e) => setValor(e.target.value)} />
          </div>
          {margenEst != null && (
            <div style={{ fontSize: 11, color: margenColor(margenEst), marginTop: 4 }}>
              Margen estimado: {margenEst.toFixed(1)}% {margenIcono(margenEst)}
            </div>
          )}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={lbl}>Notas</div>
          <textarea style={{ ...estiloInput, minHeight: 50, resize: 'vertical' }}
            value={notas} onChange={(e) => setNotas(e.target.value)} />
        </div>
        {error && (
          <div style={{ marginBottom: 12, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>
            ⚠️ {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button style={{ ...estiloBotonPrimario, opacity: guardando ? 0.6 : 1 }}
            onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Estilos chicos
// ============================================

const lbl = { fontSize: 11, fontWeight: 600, color: COLOR_GRIS, marginBottom: 4 };
const btnIcon = { background: 'transparent', border: 'none', fontSize: 14, cursor: 'pointer', padding: '4px 6px' };
